// src/utils/pizzaConfigValidator.ts
// Utilitário para validação de configuração de pizza com rastreamento de campos

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  invalidFields: string[]; // Novos campos inválidos
}

/**
 * Valida os dados de configuração da pizza antes de enviar ao backend
 * @param data - Dados do formulário
 * @returns Objeto com isValid, array de erros e campos inválidos
 */
export const validatePizzaConfigBeforeSubmit = (data: any): ValidationResult => {
  const errors: string[] = [];
  const invalidFields: string[] = [];

  // ========== CONFIGURAÇÕES ==========
  if (!data.NumberItems || data.NumberItems === '' || data.NumberItems === '0') {
    errors.push('⚠️ Configuração: O número de sabores é obrigatório e deve ser maior que zero.');
    invalidFields.push('NumberItems');
  }

  if (!data.MaximumItems || data.MaximumItems === '' || data.MaximumItems === '0') {
    errors.push('⚠️ Configuração: O máximo de ingredientes por sabor é obrigatório e deve ser maior que zero.');
    invalidFields.push('MaximumItems');
  }

  // ✅ REMOVIDO: Validação de MinimumPrice agora é opcional
  // O campo pode ser 0 ou vazio sem causar erro de validação

  if (!data.TabletTerminals || data.TabletTerminals === '') {
    errors.push('⚠️ Configuração: O campo Terminais de Tablet é obrigatório.');
    invalidFields.push('TabletTerminals');
  }

  // ========== REGRAS DE VENDA ==========
  const sellingRules = data.SellingRules?.toString();
  if (!sellingRules || !['1', '2', '3'].includes(sellingRules)) {
    errors.push('⚠️ Regras de Venda: Selecione uma regra (Maior valor, Valor médio ou Somatório).');
    invalidFields.push('SellingRules');
  }

  // ========== EXTRAS ==========
  if (data.NumberFreetems === undefined || data.NumberFreetems === '') {
    errors.push('⚠️ Extras: O número de extras grátis é obrigatório.');
    invalidFields.push('NumberFreetems');
  }

  if (data.NumberFreelngredients === undefined || data.NumberFreelngredients === '') {
    errors.push('⚠️ Extras: O número de ingredientes grátis é obrigatório.');
    invalidFields.push('NumberFreelngredients');
  }

  return {
    isValid: errors.length === 0,
    errors,
    invalidFields
  };
};

/**
 * Transforma os dados do formulário para o formato esperado pelo backend
 * @param data - Dados do formulário
 * @returns Dados transformados
 */
export const transformPizzaConfigData = (data: any) => {
  // CRÍTICO: Garantir tipos corretos
  const sellingRulesNumber = typeof data.SellingRules === 'string' 
    ? parseInt(data.SellingRules) 
    : Number(data.SellingRules);

  const tabletTerminalsString = data.TabletTerminals?.toString() || "1";

  // ✅ MinimumPrice: Se vazio ou "0,00", envia 0
  const minimumPrice = data.MinimumPrice 
    ? parseFloat(data.MinimumPrice.toString().replace(',', '.')) 
    : 0;

  return {
    SellingRules: sellingRulesNumber, // Deve ser número (1, 2 ou 3)
    TabletTerminals: tabletTerminalsString, // Deve ser string
    MinimumPrice: minimumPrice, // Agora pode ser 0
    NumberItems: Number(data.NumberItems),
    MaximumItems: Number(data.MaximumItems),
    NumberFreetems: Number(data.NumberFreetems),
    NumberFreelngredients: Number(data.NumberFreelngredients),
    // Converter booleans para 0 ou 1
    Free: data.Free === true ? 1 : 0,
    IsTablet: data.IsTablet === true ? 1 : 0,
    CloseAfterFlavor: data.CloseAfterFlavor === true ? 1 : 0,
    PayBorderbigint: data.PayBorderbigint === true ? 1 : 0,
    ChangeDescription: data.ChangeDescription === true ? 1 : 0,
  };
};

/**
 * Log de debug para verificar transformação de dados
 * Remover em produção
 */
export const logPizzaConfigTransformation = (original: any, transformed: any) => {
  console.group('🍕 Pizza Config - Transformação de Dados');
  console.log('📋 Dados originais:', original);
  console.log('📦 Dados transformados:', transformed);
  console.log('');
  console.log('🔍 SellingRules:');
  console.log('  - Tipo original:', typeof original.SellingRules);
  console.log('  - Valor original:', original.SellingRules);
  console.log('  - Tipo transformado:', typeof transformed.SellingRules);
  console.log('  - Valor transformado:', transformed.SellingRules);
  console.log('  - ✅ Deve ser número (1, 2 ou 3)');
  console.log('');
  console.log('🔍 TabletTerminals:');
  console.log('  - Tipo original:', typeof original.TabletTerminals);
  console.log('  - Valor original:', original.TabletTerminals);
  console.log('  - Tipo transformado:', typeof transformed.TabletTerminals);
  console.log('  - Valor transformado:', transformed.TabletTerminals);
  console.log('  - ✅ Deve ser string');
  console.log('');
  console.log('🔍 MinimumPrice:');
  console.log('  - Tipo original:', typeof original.MinimumPrice);
  console.log('  - Valor original:', original.MinimumPrice);
  console.log('  - Tipo transformado:', typeof transformed.MinimumPrice);
  console.log('  - Valor transformado:', transformed.MinimumPrice);
  console.log('  - ✅ Agora é opcional (pode ser 0)');
  console.groupEnd();
};